/**
 * The delivery status and end of life a Client Admin is proposing for each
 * programme, and the write that applies them.
 *
 * @module components/admin/useDeliveryStatus
 */

import { useMemo, useState } from "react";

import type { ProgrammePlatformClient, ProgrammeSummary } from "@/platform";
import type { ProgrammeDeliveryStatus } from "@/server/types";

export interface DeliveryFeedback {
  tone: "success" | "warning";
  message: string;
}

export interface DeliveryStatusState {
  /** The status each programme would be given, edits over what is on file. */
  drafts: Readonly<Record<string, ProgrammeDeliveryStatus>>;
  /** The end of life each programme would be given, "" for none. */
  endOfLifeDrafts: Readonly<Record<string, string>>;
  /** The programme whose write is in flight, or null. */
  busyProgrammeId: string | null;
  feedback: DeliveryFeedback | null;
  setDraft: (programmeId: string, status: ProgrammeDeliveryStatus) => void;
  setEndOfLifeDraft: (programmeId: string, endOfLife: string) => void;
  /** Writes whichever of the two drafts differs from what is on file. */
  apply: (programme: ProgrammeSummary) => void;
}

/** How the status reads once it is on a row. */
export function deliveryLabel(
  status: ProgrammeDeliveryStatus | undefined,
  eligible: boolean,
  retired = false,
): string {
  if (!eligible) {
    return "Not applicable until approval";
  }
  if (retired) {
    return "Retired";
  }
  if (!status) {
    return "Not set";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * @param client - The platform client to write through.
 * @param programmes - What the platform currently reports.
 * @param onChanged - Reloads the caller's list after a successful write.
 * @returns The drafts, and the write.
 *
 * @example
 * ```tsx
 * const delivery = useDeliveryStatus(client, programmes, refreshPlatformWorkspace);
 * ```
 */
export function useDeliveryStatus(
  client: ProgrammePlatformClient,
  programmes: readonly ProgrammeSummary[],
  onChanged: () => Promise<void>,
): DeliveryStatusState {
  // Only the statuses the operator has changed. The effective draft is derived
  // from these over what the platform reports, so a refresh of `programmes`
  // updates every untouched row without discarding an edit in progress.
  const [edits, setEdits] = useState<Readonly<Record<string, ProgrammeDeliveryStatus>>>({});
  const [endOfLifeEdits, setEndOfLifeEdits] = useState<Readonly<Record<string, string>>>({});
  const [busyProgrammeId, setBusyProgrammeId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<DeliveryFeedback | null>(null);

  const drafts = useMemo(
    () =>
      Object.fromEntries(
        programmes.map((programme) => [
          programme.id,
          edits[programme.id] ?? programme.deliveryStatus ?? "inactive",
        ]),
      ),
    [edits, programmes],
  );
  const endOfLifeDrafts = useMemo(
    () =>
      Object.fromEntries(
        programmes.map((programme) => [
          programme.id,
          endOfLifeEdits[programme.id] ?? programme.endOfLife ?? "",
        ]),
      ),
    [endOfLifeEdits, programmes],
  );

  const apply = (programme: ProgrammeSummary): void => {
    const status = drafts[programme.id] ?? "inactive";
    const endOfLife = endOfLifeDrafts[programme.id] ?? "";
    const statusChanged = status !== (programme.deliveryStatus ?? "inactive");
    const endOfLifeChanged = endOfLife !== (programme.endOfLife ?? "");
    const changes = [
      statusChanged && `is now ${deliveryLabel(status, true).toLowerCase()}`,
      endOfLifeChanged && (endOfLife ? `retires on ${endOfLife}` : "has no end of life"),
    ].filter(Boolean);
    setBusyProgrammeId(programme.id);
    setFeedback(null);
    const write = async (): Promise<void> => {
      if (statusChanged) {
        await client.setProgrammeDeliveryStatus({ programmeId: programme.id, status });
      }
      if (endOfLifeChanged) {
        await client.setProgrammeEndOfLife({
          programmeId: programme.id,
          ...(endOfLife ? { endOfLife } : {}),
        });
      }
    };
    write()
      .then(async () => {
        setFeedback({
          tone: "success",
          message: `${programme.title} ${changes.join(" and ")}.`,
        });
        setEndOfLifeEdits(({ [programme.id]: _applied, ...rest }) => rest);
        try {
          await onChanged();
        } catch {
          setFeedback({
            tone: "warning",
            message: `${programme.title} updated, but refreshing the list failed.`,
          });
        }
      })
      .catch((cause: unknown) => {
        setFeedback({
          tone: "warning",
          message: cause instanceof Error ? cause.message : "Unable to update delivery.",
        });
      })
      .finally(() => {
        setBusyProgrammeId(null);
      });
  };

  return {
    drafts,
    endOfLifeDrafts,
    busyProgrammeId,
    feedback,
    setDraft: (programmeId, status) => {
      setEdits((current) => ({ ...current, [programmeId]: status }));
    },
    setEndOfLifeDraft: (programmeId, endOfLife) => {
      setEndOfLifeEdits((current) => ({ ...current, [programmeId]: endOfLife }));
    },
    apply,
  };
}
